import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import * as Icons from "lucide-react";
import { BookOpen, ClipboardCheck, MessageCircle, ChevronDown, ChevronUp, Check, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Tracks() {
  const { user } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [progress, setProgress] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [detailed, setDetailed] = useState({}); // slug -> detail
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [errorMsg, setErrorMsg] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    setStatus("loading");
    setErrorMsg("");
    try {
      const [t, p] = await Promise.all([api.get("/tracks"), api.get("/progress")]);
      setTracks(Array.isArray(t.data) ? t.data : []);
      setProgress(Array.isArray(p.data) ? p.data : []);
      setStatus("ready");
    } catch (err) {
      // Network or 5xx — 401s are handled by the global interceptor (bounces
      // to /login). Anything else surfaces here so the page is never silently
      // blank.
      const detail = err?.response?.data?.detail;
      setErrorMsg(
        typeof detail === "string"
          ? detail
          : (err?.message || "We couldn't load your tracks. Please try again.")
      );
      setStatus("error");
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (slug) => {
    const next = expanded === slug ? null : slug;
    setExpanded(next);
    if (next && !detailed[slug]) {
      try {
        const { data } = await api.get(`/tracks/${slug}`);
        setDetailed((prev) => ({ ...prev, [slug]: data }));
      } catch (err) {
        console.error("Tracks: failed to load detail for", slug, err);
      }
    }
  };

  const progressMap = Object.fromEntries(progress.map((p) => [p.track_slug, p]));

  const startSession = (slug, session) => {
    navigate(`/tracks/${slug}?start=${session}`);
  };

  return (
    <div className="bg-tcpi-bg min-h-screen" data-testid="tracks-page">
      <section className="py-16 px-6 md:px-12 max-w-7xl mx-auto">
        <div className="overline mb-3">The Curriculum</div>
        <h1
          className="text-4xl md:text-5xl text-[#F7F5F0] mb-4"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          The Seven Tracks
        </h1>
        <p className="text-[#D1CFC9] max-w-2xl mb-4">
          Each track is delivered over a Monday–Wednesday–Friday rhythm. Tap any track to start
          it now, or tap the preview chevron to see what it covers first.
        </p>
        <p className="text-xs text-[#8A8782] mb-12 italic">
          You don't have to start at Track 1. Start where you are.
        </p>

        {status === "loading" && (
          <div className="card-bento text-center text-[#8A8782]" data-testid="tracks-loading">
            Loading your tracks…
          </div>
        )}

        {status === "error" && (
          <div className="card-bento" data-testid="tracks-error">
            <div className="text-[#F7F5F0] mb-2" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              We couldn't load your tracks.
            </div>
            <p className="text-sm text-[#D1CFC9] mb-4">{errorMsg}</p>
            <button
              onClick={load}
              className="btn-primary text-sm inline-flex items-center gap-2 py-2 px-4"
              data-testid="tracks-retry-btn"
            >
              Try again
            </button>
          </div>
        )}

        {status === "ready" && tracks.length === 0 && (
          <div className="card-bento text-center text-[#D1CFC9]" data-testid="tracks-empty">
            <p className="mb-1">Your curriculum isn't available right now.</p>
            <p className="text-xs text-[#8A8782]">
              If you're enrolled through a cohort, contact your facilitator. Otherwise sign out and back in.
            </p>
          </div>
        )}

        {status === "ready" && tracks.length > 0 && (
        <div className="grid lg:grid-cols-2 gap-6">
          {tracks.map((t) => {
            const Icon = Icons[t.icon] || Icons.BookOpen;
            const p = progressMap[t.slug] || {};
            const open = expanded === t.slug;
            const detail = detailed[t.slug];
            const doneCount = ["monday", "wednesday", "friday"].filter((s) => p[s]).length;

            return (
              <div
                key={t.slug}
                className={`card-bento transition overflow-hidden ${open ? "border-[#C9A961]" : "hover:border-[#C9A961]/60"}`}
                data-testid={`track-${t.slug}`}
              >
                <TrackThumb slug={t.slug} program={user?.program} title={t.title} />
                {/* Whole header is a link straight into the track. Preview
                    chevron lives in the corner and stops propagation so it
                    doesn't also fire the navigation. */}
                <div className="flex items-start justify-between gap-4 mb-3">
                  <Link
                    to={`/tracks/${t.slug}?start=monday`}
                    className="flex items-center gap-3 flex-1 min-w-0 group"
                    data-testid={`track-open-${t.slug}`}
                  >
                    <Icon className="w-7 h-7 text-[#C9A961] shrink-0" strokeWidth={1.4} />
                    <div className="min-w-0">
                      <div className="overline text-xs">Track {t.number}</div>
                      <h2
                        className="text-2xl text-[#F7F5F0] mt-1 group-hover:text-[#C9A961] transition"
                        style={{ fontFamily: "Cormorant Garamond, serif" }}
                      >
                        {t.title}
                      </h2>
                    </div>
                  </Link>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggle(t.slug); }}
                    className="shrink-0 p-1 -m-1 rounded hover:bg-[#C9A961]/10 transition"
                    aria-label={open ? "Hide details" : "Preview details"}
                    data-testid={`track-toggle-${t.slug}`}
                  >
                    {open ? (
                      <ChevronUp className="w-5 h-5 text-[#C9A961]" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-[#8A8782]" />
                    )}
                  </button>
                </div>

                <Link to={`/tracks/${t.slug}?start=monday`} className="block" data-testid={`track-body-${t.slug}`}>
                  <div className="text-xs text-[#C9A961] mb-3 tracking-wide">
                    {t.course_code} · "{t.course_title}"
                  </div>
                  <p className="text-sm text-[#D1CFC9] leading-relaxed">{t.purpose}</p>
                </Link>

                {/* Progress dots + Open button */}
                <div className="mt-5 flex items-center justify-between border-t border-[#C9A961]/15 pt-4 gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#8A8782]">{doneCount}/3 sessions</span>
                    <div className="flex gap-1.5">
                      {["monday", "wednesday", "friday"].map((s) => (
                        <div
                          key={s}
                          className={`w-2 h-2 rounded-full ${p[s] ? "bg-[#C9A961]" : "bg-[#C9A961]/20"}`}
                        />
                      ))}
                    </div>
                  </div>
                  <Link
                    to={`/tracks/${t.slug}?start=monday`}
                    className="btn-primary text-xs inline-flex items-center gap-1.5 py-1.5 px-3"
                    data-testid={`open-track-btn-${t.slug}`}
                  >
                    {doneCount > 0 ? "Continue track" : "Open track"}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>

                {open && (
                  <div className="mt-6 pt-6 border-t border-[#C9A961]/15 space-y-6" data-testid={`track-detail-${t.slug}`}>
                    {detail ? (
                      <>
                        <div>
                          <div className="overline mb-2">Weekly theme</div>
                          <p className="text-[#F7F5F0] italic" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                            {detail.weekly_theme}
                          </p>
                        </div>

                        <div>
                          <div className="overline mb-2">What you'll learn</div>
                          <ul className="space-y-1.5">
                            {detail.objectives?.slice(0, 5).map((o) => (
                              <li key={o} className="flex items-start gap-2 text-sm text-[#D1CFC9]">
                                <Check className="w-4 h-4 text-[#C9A961] mt-0.5 shrink-0" />
                                <span>{o}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div>
                          <div className="overline mb-2">Key concepts</div>
                          <div className="flex flex-wrap gap-2">
                            {detail.vocabulary?.map((v) => (
                              <span
                                key={v}
                                className="px-2.5 py-1 text-xs border border-[#C9A961]/30 text-[#C9A961] rounded-full"
                              >
                                {v}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div>
                          <div className="overline mb-3">Choose where to begin</div>
                          <div className="grid sm:grid-cols-3 gap-3">
                            <SessionStartButton
                              icon={BookOpen}
                              label="Mon"
                              title="Learn & Reflect"
                              desc="Core reading + journal"
                              done={p.monday}
                              onClick={() => startSession(t.slug, "monday")}
                              testid={`start-monday-${t.slug}`}
                            />
                            <SessionStartButton
                              icon={ClipboardCheck}
                              label="Wed"
                              title="Apply & Assess"
                              desc="4-part assessment"
                              done={p.wednesday}
                              onClick={() => startSession(t.slug, "wednesday")}
                              testid={`start-wednesday-${t.slug}`}
                            />
                            <SessionStartButton
                              icon={MessageCircle}
                              label="Fri"
                              title="Share & Lead"
                              desc="Round-table discussion"
                              done={p.friday}
                              onClick={() => startSession(t.slug, "friday")}
                              testid={`start-friday-${t.slug}`}
                            />
                          </div>
                        </div>

                        <div className="flex justify-end">
                          <Link
                            to={`/tracks/${t.slug}`}
                            className="text-sm text-[#C9A961] hover:underline"
                            data-testid={`open-full-${t.slug}`}
                          >
                            Open the full track →
                          </Link>
                        </div>
                      </>
                    ) : (
                      <div className="text-[#8A8782] text-sm">Loading details…</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        )}
      </section>
    </div>
  );
}

function SessionStartButton({ icon: Icon, label, title, desc, done, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className="text-left p-4 rounded-md border border-[#C9A961]/25 hover:border-[#C9A961] hover:bg-[#C9A961]/5 transition group"
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-5 h-5 text-[#C9A961]" strokeWidth={1.5} />
        {done && <Check className="w-4 h-4 text-[#C9A961]" />}
      </div>
      <div className="overline text-[10px] mb-1">{label}</div>
      <div className="text-[#F7F5F0] text-sm font-medium group-hover:text-[#C9A961] transition">
        {title}
      </div>
      <div className="text-xs text-[#8A8782] mt-1">{desc}</div>
    </button>
  );
}

function TrackThumb({ slug, program, title }) {
  // Fail-safe wrapper: if the per-program hero file is missing, just render
  // nothing. The rest of the card already looks great without it.
  const [failed, setFailed] = useState(false);
  if (!slug || !program || failed) return null;
  return (
    <div className="relative -mx-8 -mt-8 mb-6 h-32 md:h-40 overflow-hidden border-b border-[#C9A961]/15 bg-[#1A1816]">
      <img
        src={`/images/tracks/${program}/${slug}.png`}
        alt={`Editorial illustration for ${title}`}
        loading="lazy"
        onError={() => setFailed(true)}
        className="w-full h-full object-cover"
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#1A1816] via-transparent to-transparent" />
    </div>
  );
}
